import { Box, SxProps, Theme } from '@mui/material';
import { useIntl } from 'react-intl';

import { BookObjectInfoFragment } from 'types/api/tickets.types';

import InfoListDisplay from './InfoListDisplay';

interface Props {
  bookObjectsInfo?: Array<BookObjectInfoFragment> | BookObjectInfoFragment;
  hasGroupedTickets?: boolean;
  sx?: SxProps<Theme>;
}

type BookingInfo = {
  section: Array<string>;
  parent: Array<string>;
  own: Array<string>;
  objectType: string;
};

const SeatingInfoGroup = ({
  bookObjectsInfo,
  hasGroupedTickets,
  sx,
}: Props) => {
  const intl = useIntl();

  if (!bookObjectsInfo) {
    return null;
  }

  const bookObjectsArray = Array.isArray(bookObjectsInfo)
    ? bookObjectsInfo
    : [bookObjectsInfo];

  if (bookObjectsArray.length === 0) {
    return null;
  }

  const groupedInfo = bookObjectsArray.reduce(
    (acc: { [key: string]: BookingInfo }, obj) => {
      const objectType = obj?.objectType?.toLowerCase();
      if (!objectType) return acc;

      if (!acc[objectType]) {
        acc[objectType] = {
          section: [],
          parent: [],
          own: [],
          objectType: objectType,
        };
      }

      if (obj?.section) {
        acc[objectType].section.push(obj.section);
      }
      if (obj?.parent) {
        acc[objectType].parent.push(obj.parent);
      }
      if (obj?.own) {
        acc[objectType].own.push(obj.own);
      }

      return acc;
    },
    {},
  );

  return (
    <Box
      sx={{
        display: 'flex',
        py: { xs: 1.5, md: 2 },
        px: 2,
        gap: 2,
        borderTop: '1px solid',
        borderColor: 'grey.800',
        flexDirection: 'column',
        width: '100%',
        borderBottomLeftRadius: hasGroupedTickets ? 0 : 16,
        borderBottomRightRadius: hasGroupedTickets ? 0 : 16,
        ...sx,
      }}
    >
      {Object.values(groupedInfo).map((info, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
          }}
        >
          <InfoListDisplay
            title={intl.formatMessage({ id: 'seat_section.label' })}
            values={info.section}
          />

          <Box
            sx={{
              display: 'flex',
              flex: 1,
            }}
          >
            {!!info?.parent?.length && (
              <InfoListDisplay
                title={intl.formatMessage({ id: 'seat_parent.label' })}
                values={info.parent}
              />
            )}

            <InfoListDisplay
              title={intl.formatMessage({ id: `seat_type.${info.objectType}` })}
              values={info.own}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default SeatingInfoGroup;
